import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { LocationTreePicker, type LocationTreeRow } from "../ui/LocationTreePicker";
import { trpc } from "../../lib/trpc";

type Severity = "low" | "medium" | "high" | "critical";
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const SEV_OPTS = Object.entries(SEV_LABELS) as [Severity, string][];

function useLocations(open: boolean): LocationTreeRow[] {
  const q = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 30_000 });
  return (q.data ?? []) as unknown as LocationTreeRow[];
}
function useAssignable(open: boolean) {
  const q = trpc.maintenance.assignableUsers.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  return q.data ?? [];
}

// ── Bakım emri oluşturma ─────────────────────────────────────────────────
interface OrderCreateProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string, code: string) => void;
  /** Arızadan dönüştürme: ön-seçili arıza (başlık/lokasyon/önem devralınır). */
  fault?: { id: string; label: string; locationId: string | null; severity: string | null } | null;
}

export function MaintenanceOrderCreateDrawer({ open, onClose, onCreated, fault }: OrderCreateProps) {
  const qc = useQueryClient();
  const locations = useLocations(open);
  const users = useAssignable(open);
  const createMut = trpc.maintenance.order.create.useMutation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Severity | "">("");
  const [locationId, setLocationId] = useState<string | null>(null);
  const [assignedToId, setAssignedToId] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(fault ? `Arıza: ${fault.label}` : "");
      setDescription("");
      setPriority((fault?.severity as Severity) ?? "");
      setLocationId(fault?.locationId ?? null);
      setAssignedToId(""); setPlannedDate(""); setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fault?.id]);

  async function save() {
    setErr(null);
    if (!title.trim()) return setErr("Başlık zorunlu.");
    try {
      const res = await createMut.mutateAsync({
        type: "corrective",
        title: title.trim(),
        description: description.trim() || null,
        priority: priority || null,
        locationId: locationId ?? null,
        assignedToId: assignedToId || null,
        plannedDate: plannedDate ? new Date(plannedDate).toISOString() : null,
        faultReportId: fault?.id ?? null,
      });
      await qc.invalidateQueries();
      onCreated?.(res.id, res.code);
      onClose();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title={fault ? "Arızayı Bakım Emrine Dönüştür" : "Yeni Bakım Emri"}
      subtitle={fault ? `Arıza: ${fault.label}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>Vazgeç</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={createMut.isPending}>
            <Icon name="wrench" size={14} />{createMut.isPending ? "Oluşturuluyor…" : "Bakım Emri Oluştur"}
          </button>
        </>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <div className="form-field">
        <label className="lbl" htmlFor="mo-title">Başlık <span className="req">*</span></label>
        <input id="mo-title" className="form-input" type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Örn. Fırın bandı rulman değişimi" />
      </div>
      <div className="form-field">
        <label className="lbl">Ekipman / Lokasyon</label>
        <LocationTreePicker locations={locations} selected={locationId} onSelect={setLocationId} maxHeight={180} />
      </div>
      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="mo-prio">Öncelik</label>
          <select id="mo-prio" className="form-select" value={priority} onChange={(e) => setPriority(e.target.value as Severity | "")}>
            <option value="">—</option>
            {SEV_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="mo-assign">Atanan Teknisyen</label>
          <select id="mo-assign" className="form-select" value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)}>
            <option value="">— Atanmadı —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="mo-planned">Planlanan Tarih</label>
        <input id="mo-planned" className="form-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="mo-desc">Açıklama</label>
        <textarea id="mo-desc" className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} placeholder="Yapılacak iş / arıza detayı…" />
      </div>
    </Drawer>
  );
}

// ── Tesis-geneli arıza kaydı ─────────────────────────────────────────────
interface FaultCreateProps { open: boolean; onClose: () => void; onCreated?: () => void; }

export function FaultCreateDrawer({ open, onClose, onCreated }: FaultCreateProps) {
  const qc = useQueryClient();
  const locations = useLocations(open);
  const reportMut = trpc.maintenance.reportFault.useMutation();

  const [locationId, setLocationId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [severity, setSeverity] = useState<Severity | "">("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) { setLocationId(null); setLabel(""); setSeverity(""); setDescription(""); setErr(null); } }, [open]);

  async function save() {
    setErr(null);
    if (!locationId) return setErr("Arızanın yerini (lokasyon) seçin.");
    if (!label.trim()) return setErr("Arıza tanımı zorunlu.");
    try {
      await reportMut.mutateAsync({ locationId, faultLabel: label.trim(), severity: severity || null, description: description.trim() || null });
      await qc.invalidateQueries();
      onCreated?.();
      onClose();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Arıza Kaydı (Tesis Geneli)"
      subtitle="Herhangi bir lokasyon/ekipman için arıza bildir"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={reportMut.isPending}>Vazgeç</button>
          <button type="button" className="btn-md danger" onClick={save} disabled={reportMut.isPending}>
            <Icon name="alertTriangle" size={14} />{reportMut.isPending ? "Kaydediliyor…" : "Arıza Kaydet"}
          </button>
        </>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <div className="form-field">
        <label className="lbl">Arızanın Yeri <span className="req">*</span></label>
        <LocationTreePicker locations={locations} selected={locationId} onSelect={setLocationId} maxHeight={200} />
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="fc-label">Arıza Tanımı <span className="req">*</span></label>
        <input id="fc-label" className="form-input" type="text" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={160} placeholder="Örn. Soğutucu kompresör arızası" />
      </div>
      <div className="form-field" style={{ maxWidth: 220 }}>
        <label className="lbl" htmlFor="fc-sev">Önem</label>
        <select id="fc-sev" className="form-select" value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
          <option value="">—</option>
          {SEV_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="fc-desc">Açıklama</label>
        <textarea id="fc-desc" className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={1000} placeholder="Arıza detayı…" />
      </div>
      <div style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>
        Kayıttan sonra listeden ek (fotoğraf/PDF) yükleyebilir, arızayı bakım emrine dönüştürebilirsiniz.
      </div>
    </Drawer>
  );
}

// ── Periyodik bakım planı (oluştur / düzenle) ───────────────────────────
interface PlanFormProps {
  open: boolean;
  onClose: () => void;
  /** Düzenleme: mevcut plan. */
  plan?: { id: string; title: string; description: string | null; intervalDays: number; nextDueAt: string; assignedToId?: string | null; locationId?: string } | null;
  initialLocations?: LocationTreeRow[];
}

export function PlanFormDrawer({ open, onClose, plan }: PlanFormProps) {
  const qc = useQueryClient();
  const locations = useLocations(open);
  const users = useAssignable(open);
  const createMut = trpc.maintenance.plan.create.useMutation();
  const updateMut = trpc.maintenance.plan.update.useMutation();
  const isEdit = !!plan;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState<string | null>(null);
  const [intervalDays, setIntervalDays] = useState("30");
  const [assignedToId, setAssignedToId] = useState("");
  const [firstDueAt, setFirstDueAt] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(plan?.title ?? "");
      setDescription(plan?.description ?? "");
      setLocationId(plan?.locationId ?? null);
      setIntervalDays(plan ? String(plan.intervalDays) : "30");
      setAssignedToId(plan?.assignedToId ?? "");
      setFirstDueAt(plan ? plan.nextDueAt.slice(0, 10) : "");
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.id]);

  async function save() {
    setErr(null);
    const interval = Math.floor(Number(intervalDays));
    if (!title.trim()) return setErr("Başlık zorunlu.");
    if (!Number.isFinite(interval) || interval < 1) return setErr("Periyot en az 1 gün olmalı.");
    try {
      if (isEdit && plan) {
        await updateMut.mutateAsync({
          id: plan.id, title: title.trim(), description: description.trim() || null,
          intervalDays: interval, assignedToId: assignedToId || null,
          nextDueAt: firstDueAt ? new Date(firstDueAt).toISOString() : null,
        });
      } else {
        if (!locationId) return setErr("Ekipman/lokasyon seçin.");
        await createMut.mutateAsync({
          title: title.trim(), description: description.trim() || null,
          locationId, intervalDays: interval, assignedToId: assignedToId || null,
          firstDueAt: firstDueAt ? new Date(firstDueAt).toISOString() : null,
        });
      }
      await qc.invalidateQueries();
      onClose();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? "Bakım Planını Düzenle" : "Yeni Periyodik Bakım Planı"}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending || updateMut.isPending}>Vazgeç</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={createMut.isPending || updateMut.isPending}>
            <Icon name="check" size={14} />{createMut.isPending || updateMut.isPending ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <div className="form-field">
        <label className="lbl" htmlFor="pl-title">Başlık <span className="req">*</span></label>
        <input id="pl-title" className="form-input" type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Örn. Fırın bandı aylık yağlama" />
      </div>
      {!isEdit ? (
        <div className="form-field">
          <label className="lbl">Ekipman / Lokasyon <span className="req">*</span></label>
          <LocationTreePicker locations={locations} selected={locationId} onSelect={setLocationId} maxHeight={180} />
        </div>
      ) : null}
      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="pl-interval">Periyot (gün) <span className="req">*</span></label>
          <input id="pl-interval" className="form-input" type="number" min={1} max={3650} value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="pl-due">{isEdit ? "Sonraki Bakım Tarihi" : "İlk Bakım Tarihi"}</label>
          <input id="pl-due" className="form-input" type="date" value={firstDueAt} onChange={(e) => setFirstDueAt(e.target.value)} />
          {!isEdit ? <span className="help">Boş bırakılırsa bugün + periyot.</span> : null}
        </div>
      </div>
      <div className="form-field" style={{ maxWidth: 320 }}>
        <label className="lbl" htmlFor="pl-assign">Atanan Teknisyen</label>
        <select id="pl-assign" className="form-select" value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)}>
          <option value="">— Atanmadı —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="pl-desc">Açıklama / Kontrol Listesi</label>
        <textarea id="pl-desc" className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} placeholder="Yapılacak işler…" />
      </div>
    </Drawer>
  );
}

// ── Yedek parça türlerini organize et ────────────────────────────────────
// Yedek Parçalar sekmesinde HANGİ stok kart türlerinin listeleneceğini kullanıcı seçer.
// Hiçbiri seçili değilse → tüm stok kartları listelenir (tek türe dayatma yok).
interface SparePartTypesProps { open: boolean; onClose: () => void; }

export function SparePartTypesDrawer({ open, onClose }: SparePartTypesProps) {
  const qc = useQueryClient();
  const settingsQ = trpc.maintenance.settings.get.useQuery(undefined, { enabled: open, staleTime: 30_000 });
  const saveMut = trpc.maintenance.settings.setSparePartTypes.useMutation();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open && settingsQ.data) setSel(new Set(settingsQ.data.sparePartTypeIds));
    if (open) setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settingsQ.data]);

  const types = settingsQ.data?.stockTypes ?? [];
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function save() {
    setErr(null);
    try {
      await saveMut.mutateAsync({ typeIds: [...sel] });
      await qc.invalidateQueries();
      onClose();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="md"
      title="Yedek Parça Türleri"
      subtitle="Yedek Parçalar sekmesinde gösterilecek stok kart türleri"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={saveMut.isPending}>Vazgeç</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={saveMut.isPending || settingsQ.isLoading}>
            <Icon name="check" size={14} />{saveMut.isPending ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <p className="spt-note">
        İşaretlenen kart türlerindeki stok kartları yedek parça listesinde gösterilir. Hiçbiri seçilmezse <b>tüm stok kartları</b> listelenir.
        Yeni tür eklemek için <b>Ayarlar → Kart Türleri</b> (stok) kullanılır.
      </p>
      {settingsQ.isLoading ? (
        <div className="mt-empty">Yükleniyor…</div>
      ) : types.length === 0 ? (
        <div className="mt-empty">Tanımlı stok kart türü yok — Ayarlar → Kart Türleri'nden ekleyin.</div>
      ) : (
        <>
          <div className="spt-toolbar">
            <button type="button" className="btn-md ghost" onClick={() => setSel(new Set(types.map((t) => t.id)))}>Tümünü Seç</button>
            <button type="button" className="btn-md ghost" onClick={() => setSel(new Set())}>Temizle</button>
            <span className="sp" />
            <span className="spt-count">{sel.size} / {types.length} seçili</span>
          </div>
          <div className="spt-list">
            {types.map((t) => (
              <label key={t.id} className="spt-row">
                <input type="checkbox" checked={sel.has(t.id)} onChange={() => toggle(t.id)} />
                <span className="name">{t.name}</span>
                <span className="code">{t.code}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
}
